/**
 * Dashboard page: per-campaign day-parting scheduler (feature #74).
 *
 * URL: /campaigns/dayparting
 *   (TanStack Router — programmatic tree, path mirrors filename.)
 *
 * This page lets the operator:
 *   1. Pick a campaign (from the `?campaign=` search param or the input).
 *   2. Pick an IANA timezone (default = browser tz).
 *   3. Paint a 7×24 active-hours grid by clicking/dragging cells.
 *   4. See the projected rule list and delete individual rules.
 *   5. Save → atomic PUT /api/v1/campaign-dayparting/:campaignId.
 *   6. Clear → DELETE /api/v1/campaign-dayparting/:campaignId.
 *
 * Theme: emerald→sky gradient heading, Inter font, 6–8px rounded, zinc
 * borders. No emojis, no competitor names.
 */

import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { rootRoute } from "./__root";
import { DaypartingGrid } from "../features/dayparting/DaypartingGrid";
import {
	ALL_DAYS,
	ROW_LABELS,
	ROW_TO_DOW,
	cloneGrid,
	deleteDaypartingRules,
	emptyGrid,
	getBrowserTimezone,
	gridToRules,
	isValidTimezone,
	listDaypartingRules,
	listSupportedTimezones,
	replaceDaypartingRules,
	rulesToGrid,
	validateRuleInput,
} from "../features/dayparting/api";
import type {
	DayOfWeekInput,
	DaypartingRule,
	GridMatrix,
} from "../features/dayparting/api";

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function DaypartingPage() {
	const searchParams =
		typeof window !== "undefined"
			? new URLSearchParams(window.location.search)
			: new URLSearchParams();
	const initialCampaign = searchParams.get("campaign") ?? "";

	const queryClient = useQueryClient();
	const [campaignId, setCampaignId] = useState<string>(initialCampaign);
	const [committedCampaignId, setCommittedCampaignId] =
		useState<string>(initialCampaign);
	const [tz, setTz] = useState<string>(() => getBrowserTimezone());
	const [grid, setGrid] = useState<GridMatrix>(() => emptyGrid());
	const [dirty, setDirty] = useState(false);

	const timezones = useMemo(() => listSupportedTimezones(), []);

	// --------------------------------------------------------------
	// Query: list rules for the committed campaign.
	// --------------------------------------------------------------
	const rulesQuery = useQuery<DaypartingRule[]>({
		queryKey: ["dayparting", committedCampaignId],
		queryFn: () => listDaypartingRules(committedCampaignId),
		enabled: committedCampaignId.length > 0,
		staleTime: 30_000,
	});

	// When the fetched rules change (and grid hasn't been locally edited),
	// project them onto the grid.
	useEffect(() => {
		if (rulesQuery.data && !dirty) {
			setGrid(rulesToGrid(rulesQuery.data));
			// Prefer the first rule's tz as the active tz if present.
			const first = rulesQuery.data[0];
			if (first && isValidTimezone(first.tz)) setTz(first.tz);
		}
	}, [rulesQuery.data, dirty]);

	// --------------------------------------------------------------
	// Mutations
	// --------------------------------------------------------------
	const saveMutation = useMutation({
		mutationFn: async () => {
			const rules = gridToRules(grid, tz);
			// Validate each rule via the same Zod-shaped helper as the API.
			for (const r of rules) {
				const res = validateRuleInput(r);
				if (!res.ok) {
					const first = res.issues[0];
					throw new Error(`${first.path}: ${first.message}`);
				}
			}
			return replaceDaypartingRules(committedCampaignId, rules);
		},
		onSuccess: (saved) => {
			queryClient.setQueryData(["dayparting", committedCampaignId], saved);
			setDirty(false);
			toast.success("Schedule saved.");
		},
		onError: (err) => {
			toast.error(
				err instanceof Error ? err.message : "Failed to save schedule.",
			);
		},
	});

	const clearMutation = useMutation({
		mutationFn: () => deleteDaypartingRules(committedCampaignId),
		onSuccess: () => {
			queryClient.setQueryData<DaypartingRule[]>(
				["dayparting", committedCampaignId],
				[],
			);
			setGrid(emptyGrid());
			setDirty(false);
			toast.success("All rules cleared.");
		},
		onError: (err) => {
			toast.error(
				err instanceof Error ? err.message : "Failed to clear rules.",
			);
		},
	});

	// --------------------------------------------------------------
	// Handlers
	// --------------------------------------------------------------
	const handleLoadCampaign = useCallback(() => {
		const trimmed = campaignId.trim();
		if (trimmed.length === 0) {
			toast.error("Enter a campaign id first.");
			return;
		}
		setCommittedCampaignId(trimmed);
		setDirty(false);
	}, [campaignId]);

	const handleGridChange = useCallback((next: GridMatrix) => {
		setGrid(next);
		setDirty(true);
	}, []);

	const handleTzChange = useCallback((next: string) => {
		if (!isValidTimezone(next)) {
			toast.error("Invalid timezone.");
			return;
		}
		setTz(next);
		setDirty(true);
	}, []);

	const handleRemoveRule = useCallback(
		(row: number, startHour: number, endHour: number) => {
			const next = cloneGrid(grid);
			if (startHour < endHour) {
				for (let h = startHour; h < endHour; h++) next[row][h] = false;
			} else {
				// Wrap / full-day.
				for (let h = 0; h < 24; h++) next[row][h] = false;
			}
			setGrid(next);
			setDirty(true);
		},
		[grid],
	);

	// --------------------------------------------------------------
	// Derived: rules visible in the summary panel.
	// --------------------------------------------------------------
	const summaryRules = useMemo(() => {
		// Compute runs inline rather than calling gridToRules to preserve
		// display as [start,end] (inclusive-exclusive) labels.
		const out: Array<{
			row: number;
			startHour: number;
			endHour: number;
			label: string;
		}> = [];
		for (let r = 0; r < 7; r++) {
			let run: number | null = null;
			for (let c = 0; c < 24; c++) {
				if (grid[r][c] && run === null) run = c;
				if (!grid[r][c] && run !== null) {
					out.push({
						row: r,
						startHour: run,
						endHour: c,
						label: `${ROW_LABELS[r]} ${String(run).padStart(2, "0")}:00 – ${String(c).padStart(2, "0")}:00`,
					});
					run = null;
				}
			}
			if (run !== null) {
				if (run === 0) {
					out.push({
						row: r,
						startHour: 0,
						endHour: 0,
						label: `${ROW_LABELS[r]} (all day)`,
					});
				} else {
					out.push({
						row: r,
						startHour: run,
						endHour: 24,
						label: `${ROW_LABELS[r]} ${String(run).padStart(2, "0")}:00 – 24:00`,
					});
				}
			}
		}
		return out;
	}, [grid]);

	const saveDisabled =
		committedCampaignId.length === 0 ||
		saveMutation.isPending ||
		!dirty;

	// --------------------------------------------------------------
	// Render
	// --------------------------------------------------------------
	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10">
			<header className="flex flex-col gap-2">
				<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					Campaigns
				</p>
				<h1 className="text-brand-gradient text-4xl font-semibold tracking-tight sm:text-5xl">
					Day-parting schedule
				</h1>
				<p className="max-w-2xl text-sm text-zinc-500">
					Pick the hours and days when this campaign should be eligible to
					serve. Rules evaluate in the timezone you choose below.
				</p>
			</header>

			<section className="rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm">
				<div className="grid gap-4 sm:grid-cols-[2fr_2fr_auto] sm:items-end">
					<label className="flex flex-col gap-1.5">
						<span className="text-xs font-medium text-zinc-600">
							Campaign id
						</span>
						<input
							type="text"
							value={campaignId}
							onChange={(e) => setCampaignId(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleLoadCampaign();
							}}
							placeholder="e.g. camp_abc123"
							className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none transition-colors focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200"
						/>
					</label>
					<label className="flex flex-col gap-1.5">
						<span className="text-xs font-medium text-zinc-600">
							Timezone (IANA)
						</span>
						<select
							value={tz}
							onChange={(e) => handleTzChange(e.target.value)}
							className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none transition-colors focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200"
						>
							{timezones.map((z) => (
								<option key={z} value={z}>
									{z}
								</option>
							))}
						</select>
					</label>
					<button
						type="button"
						onClick={handleLoadCampaign}
						className="h-10 rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95"
					>
						Load
					</button>
				</div>
				{committedCampaignId ? (
					<p className="mt-3 text-xs text-zinc-500">
						Viewing rules for{" "}
						<span className="font-mono text-zinc-700">
							{committedCampaignId}
						</span>
						.
					</p>
				) : (
					<p className="mt-3 text-xs text-zinc-500">
						Enter a campaign id above to load or edit its schedule.
					</p>
				)}
			</section>

			<section className="flex flex-col gap-5 lg:flex-row">
				<div className="flex-1">
					<DaypartingGrid
						value={grid}
						onChange={handleGridChange}
						disabled={committedCampaignId.length === 0}
					/>
				</div>
				<aside className="w-full rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm lg:max-w-sm">
					<div className="flex items-center justify-between">
						<h3 className="text-sm font-semibold text-zinc-900">
							Active windows
						</h3>
						<span className="text-xs text-zinc-500">
							{summaryRules.length}{" "}
							{summaryRules.length === 1 ? "rule" : "rules"}
						</span>
					</div>
					{rulesQuery.isLoading ? (
						<p className="mt-4 text-xs text-zinc-500">Loading rules…</p>
					) : summaryRules.length === 0 ? (
						<p className="mt-4 text-xs text-zinc-500">
							No active hours yet. Paint the grid on the left to add
							rules, then click Save.
						</p>
					) : (
						<ul className="mt-3 flex max-h-80 flex-col gap-2 overflow-y-auto">
							{summaryRules.map((r) => (
								<li
									key={`${r.row}-${r.startHour}-${r.endHour}`}
									className="flex items-center justify-between rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs"
								>
									<div className="flex flex-col">
										<span className="font-medium text-zinc-800">
											{r.label}
										</span>
										<span className="font-mono text-[10px] text-zinc-500">
											DOW {ROW_TO_DOW[r.row]} · {tz}
										</span>
									</div>
									<button
										type="button"
										onClick={() =>
											handleRemoveRule(r.row, r.startHour, r.endHour)
										}
										className="rounded-[4px] px-2 py-1 text-[10px] font-semibold text-zinc-500 transition-colors hover:bg-zinc-200 hover:text-zinc-800"
										aria-label={`Remove rule ${r.label}`}
									>
										Remove
									</button>
								</li>
							))}
						</ul>
					)}
					<div className="mt-5 flex items-center justify-between text-xs text-zinc-500">
						<span>
							{dirty ? "Unsaved changes" : "Up to date"}
						</span>
						<span className="font-mono text-[10px]">tz: {tz}</span>
					</div>
					<div className="mt-4 flex flex-col gap-2">
						<button
							type="button"
							onClick={() => saveMutation.mutate()}
							disabled={saveDisabled}
							className="h-10 w-full rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40"
						>
							{saveMutation.isPending ? "Saving…" : "Save schedule"}
						</button>
						<button
							type="button"
							onClick={() => clearMutation.mutate()}
							disabled={
								committedCampaignId.length === 0 ||
								clearMutation.isPending
							}
							className="h-9 w-full rounded-[6px] border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40"
						>
							{clearMutation.isPending
								? "Clearing…"
								: "Clear all rules"}
						</button>
					</div>
				</aside>
			</section>

			<section className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-5 text-xs text-zinc-600">
				<p className="mb-2 font-medium uppercase tracking-wider text-zinc-500">
					Rule semantics
				</p>
				<ul className="flex flex-col gap-1">
					<li>
						dayOfWeek = <span className="font-mono">0-6</span> (Sunday=0) or{" "}
						<span className="font-mono">"{ALL_DAYS}"</span>.
					</li>
					<li>
						startHour / endHour ∈ <span className="font-mono">[0,23]</span>.
					</li>
					<li>
						start &lt; end → same-day window. start = end → 24 hours.
						start &gt; end → wraps past midnight.
					</li>
				</ul>
			</section>
		</main>
	);
}

// Suppress unused-binding diagnostics for typed-only re-exports.
export type { DayOfWeekInput };

// ---------------------------------------------------------------------------
// TanStack Router registration — programmatic tree.
// ---------------------------------------------------------------------------
export const campaignsDaypartingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/campaigns/dayparting",
	component: DaypartingPage,
});

export default DaypartingPage;
